import { Space } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { AddEntityToSeasonModal } from "~pages/SeasonEntities/SeasonEntityListPage/components/AddEntityToSeasonModal.tsx";
import {
  defaultSeasonEntityExportFieldNames,
  seasonEntityExportFields,
} from "~pages/SeasonEntities/SeasonEntityListPage/constants/exportFields.ts";
import { useDeleteSeasonEntity } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useDeleteSeasonEntity.ts";
import { useExportSeasonEntities } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useExportSeasonEntities.ts";
import { useFetchSeasonEntities } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useFetchSeasonEntities.ts";
import { useGetSeasonEntityTableColumns } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useGetSeasonEntityTableColumns.tsx";
import { useImportSeasonEntities } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useImportSeasonEntities.ts";

const SeasonEntityListPage = () => {
  const { sport, customerId, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const paginationConfig = useHandlePagination(20);
  const [searchText, setSearchText] = useState("");
  const [massEditMode, setMassEditMode] = useState(false);
  const [isAddEntityToSeasonModalOpen, setIsAddEntityToSeasonModalOpen] = useState(false);
  const { seasonEntityPermissionName } = usePermissionsByCompetitionName();
  const seasonEntityPermission = seasonEntityPermissionName as PermissionNames;
  const { hasPermission } = useUserPermissions();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.seasonEntities.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.seasonEntities.listPage.importConfig;
  const columns = useGetSeasonEntityTableColumns(massEditMode);
  const { mutateAsync: startImporting } = useImportSeasonEntities();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportSeasonEntities();
  const { mutateAsync: deleteSeasonEntity, isPending: isDeletePending } = useDeleteSeasonEntity();

  const listParams = useMemo(
    () => ({
      include: "entities,conferences,divisions",
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      entityNameFullLocalContains: searchText,
    }),
    [paginationConfig.offset, paginationConfig.pageSize, searchText],
  );

  const { data, refetch, isPending } = useFetchSeasonEntities({
    customParams: listParams,
  });
  const seasonEntityIdSet = useMemo(() => new Set(data?.data?.map(({ entityId }) => entityId) || []), [data]);

  const navigateToSeasonEntity = (entityId: string, path = "") => {
    navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/entities/${entityId}${path}`);
  };

  const canReadRoster = hasPermission(PermissionNames.SEASON_ROSTER, PermissionOperations.READ);
  const canReadStaff = hasPermission(PermissionNames.ROLES, PermissionOperations.READ);
  const onMassEditModeChangedHandler = useCallback((checked: boolean) => {
    setMassEditMode(checked);
  }, []);

  return (
    <>
      <AddEntityToSeasonModal
        isOpen={isAddEntityToSeasonModalOpen}
        setIsOpen={setIsAddEntityToSeasonModalOpen}
        reloadData={refetch}
        existingSeasonEntityIdSet={seasonEntityIdSet}
      />
      <DataTable
        handleRefresh={() => {
          refetch();
        }}
        title={formatMessage({ id: "entities" })}
        columns={columns}
        rowActions={{
          custom: {
            roster: {
              hidden: () => !canReadRoster,
              renderAction: (row) => (
                <Space onClick={() => navigateToSeasonEntity(row.entityId, "/roster")}>
                  <i className="fas fa-users mr-2" />
                  <span>{formatMessage({ id: "roster" })}</span>
                </Space>
              ),
            },
            staff: {
              hidden: () => !canReadStaff,
              renderAction: (row) => (
                <Space onClick={() => navigateToSeasonEntity(row.entityId, "/staff")}>
                  <i className="fas fa-user-tie mr-2" />
                  <span>{formatMessage({ id: "staff" })}</span>
                </Space>
              ),
            },
          },
          base: {
            manage: {
              onClick: (row) => navigateToSeasonEntity(row.entityId),
            },
            delete: {
              onClick: (row) => {
                void deleteSeasonEntity(row.entityId);
              },
              itemName: (row) => row.entity?.nameFullLocal || row.entityId,
              loading: isDeletePending,
            },
          },
        }}
        rowKey={"entityId"}
        rowNameKey={"entityId"}
        dataSource={data?.data || []}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.seasonEntities`}
        isDataLoading={isPending}
        handleAdd={() => setIsAddEntityToSeasonModalOpen(true)}
        addButtonIntlKey={"entity.add"}
        searchConfig={{
          handleSearch: setSearchText,
          searchText: searchText,
        }}
        onMassEditModeChangedHandler={onMassEditModeChangedHandler}
        massEditMode={massEditMode}
        exportConfig={{
          handleExport: startExport,
          loading: isExportPending,
          defaultFields: defaultSeasonEntityExportFieldNames,
          groupedFields: seasonEntityExportFields,
          localStorageKey: "export.seasonEntities",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: startImporting,
        }}
        permissionNames={[seasonEntityPermission]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
      />
    </>
  );
};

export default SeasonEntityListPage;
